import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Card, DarkSelect, Input, PageHeader } from "@/components/ui";

interface Segment {
  segment: string;
  count: number;
  label: string;
  description: string;
}

interface SegmentsResponse {
  segments: Segment[];
}

interface DraftState {
  id: number;
  status:
    | "awaiting_content"
    | "ready"
    | "sending"
    | "done"
    | "canceled";
  segment: string | null;
  has_content: boolean;
  queued: number | null;
  sent_count: number | null;
  failed_count: number | null;
}

interface PickedUser {
  telegram_id: number;
  username: string | null;
}

export default function Broadcast() {
  const [segment, setSegment] = useState("all");
  // Произвольный режим: рассылка точечно выбранным юзерам (для теста на себе /
  // адресной отправки). Эффективный сегмент = manual:<tid,tid,...>.
  const [manualMode, setManualMode] = useState(false);
  const [manualUsers, setManualUsers] = useState<PickedUser[]>([]);
  const [uq, setUq] = useState("");
  const [text, setText] = useState("");
  const [queued, setQueued] = useState<number | null>(null);
  const [mode, setMode] = useState<"text" | "bot">("text");
  const [btnText, setBtnText] = useState("");
  const [btnUrl, setBtnUrl] = useState("");
  const [btnAction, setBtnAction] = useState<
    "none" | "buy" | "vpn" | "renew" | "promo" | "url"
  >("none");
  const [btnPromo, setBtnPromo] = useState("");
  // Анти-двойная отправка быстрого текста: ключ стабилен для одного составления,
  // после успешной отправки перегенерируется.
  const [sendKey, setSendKey] = useState<string>(() =>
    (crypto.randomUUID?.() ?? String(Math.random())),
  );

  const botInfo = useQuery<{ username: string | null }>({
    queryKey: ["bot-info"],
    queryFn: () => api("/api/admin/bot-info"),
  });
  const promos = useQuery<{ items: { code: string }[] }>({
    queryKey: ["promo-codes", "active"],
    queryFn: () => api("/api/admin/promo-codes?active_only=true&limit=100"),
    enabled: btnAction === "promo",
  });

  // Состояние rich-format draft через бота (новый флоу).
  const [draftId, setDraftId] = useState<number | null>(null);
  const [draftState, setDraftState] = useState<DraftState | null>(null);

  const segments = useQuery<SegmentsResponse>({
    queryKey: ["broadcast", "segments"],
    queryFn: () => api("/api/admin/broadcast/segments"),
  });

  const send = useMutation({
    mutationFn: (body: {
      segment: string;
      text: string;
      button_text?: string | null;
      button_url?: string | null;
      idempotency_key?: string;
    }) =>
      api<{ queued: number; id?: number }>("/api/admin/broadcast", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (res) => {
      setQueued(res.queued);
      setText("");
      setSendKey(crypto.randomUUID?.() ?? String(Math.random()));
    },
  });

  const startDraft = useMutation({
    mutationFn: (body: {
      segment: string;
      button_text?: string | null;
      button_url?: string | null;
    }) =>
      api<DraftState>("/api/admin/broadcast/draft/start", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (res) => {
      setDraftId(res.id);
      setDraftState(res);
    },
  });

  const confirmDraft = useMutation({
    mutationFn: (id: number) =>
      api<{ queued: number }>(
        `/api/admin/broadcast/draft/${id}/confirm`,
        { method: "POST" },
      ),
    onSuccess: (res) => {
      setQueued(res.queued);
    },
  });

  const cancelDraft = useMutation({
    mutationFn: (id: number) =>
      api<DraftState>(`/api/admin/broadcast/draft/${id}/cancel`, {
        method: "POST",
      }),
    // Не обнуляем: при остановке НА ЛЕТУ раннер ещё дописывает статус — держим
    // поллинг, пока не станет canceled (покажем итог).
    onSuccess: (res) => {
      setDraftState(res);
    },
  });

  // Поллинг draft state каждые 2 сек пока статус активен.
  useEffect(() => {
    if (!draftId) return;
    if (draftState?.status === "done" || draftState?.status === "canceled") {
      return;
    }
    const t = setInterval(async () => {
      try {
        const r = await api<DraftState>(
          `/api/admin/broadcast/draft/${draftId}`,
        );
        setDraftState(r);
      } catch {
        // sluggish API — пропускаем, следующий тик попробует снова
      }
    }, 2000);
    return () => clearInterval(t);
  }, [draftId, draftState?.status]);

  const list = segments.data?.segments ?? [];
  const selected = list.find((s) => s.segment === segment);

  // Поиск юзеров для произвольного режима.
  const userSearch = useQuery<{ users: PickedUser[] }>({
    queryKey: ["bcast-user-search", uq.trim()],
    queryFn: () => api(`/api/admin/users/search?q=${encodeURIComponent(uq.trim())}`),
    enabled: manualMode && uq.trim().length >= 2,
  });
  const addUser = (u: PickedUser) => {
    setManualUsers((prev) => (prev.some((p) => p.telegram_id === u.telegram_id) ? prev : [...prev, u]));
    setUq("");
  };
  const effectiveSegment = manualMode
    ? `manual:${manualUsers.map((u) => u.telegram_id).join(",")}`
    : segment;
  const audienceReady = manualMode ? manualUsers.length > 0 : true;

  function defaultBtnLabel(a: typeof btnAction): string {
    return a === "buy"
      ? "💳 Оплатить"
      : a === "promo"
        ? "🎟 Оплатить со скидкой"
        : a === "vpn"
          ? "🛡 Открыть VPN"
          : a === "renew"
            ? "🔁 Продлить"
            : "Открыть";
  }
  function buildBtnUrl(): string | null {
    const bot = botInfo.data?.username;
    if (btnAction === "none") return null;
    if (btnAction === "url") return btnUrl.trim() || null;
    if (!bot) return null;
    if (btnAction === "promo")
      return btnPromo ? `https://t.me/${bot}?start=promo_${btnPromo}` : null;
    return `https://t.me/${bot}?start=${btnAction}`;
  }
  function btnDisabled(): boolean {
    if (btnAction === "none") return false;
    return !buildBtnUrl();
  }

  const recipientCount = manualMode ? manualUsers.length : selected?.count ?? 0;

  // Панель кнопки — общая для ОБОИХ режимов (объединение формат+кнопка).
  const buttonPanel = (
    <div className="mt-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
        Кнопка под сообщением (опционально)
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(
          [
            ["none", "Без кнопки"],
            ["buy", "💳 Оплатить"],
            ["promo", "🎟 Со скидкой"],
            ["vpn", "🛡 VPN"],
            ["renew", "🔁 Продлить"],
            ["url", "🔗 Своя ссылка"],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setBtnAction(k)}
            className={`px-3 py-1.5 rounded-lg text-xs transition ${
              btnAction === k
                ? "bg-brand-soft text-brand ring-1 ring-inset ring-brand/25 font-semibold"
                : "text-zinc-400 hover:bg-white/[0.04] bg-white/[0.02]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {btnAction !== "none" && (
        <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input
            placeholder={`Текст кнопки (по умолч. «${defaultBtnLabel(btnAction)}»)`}
            value={btnText}
            onChange={(e) => setBtnText(e.target.value)}
          />
          {btnAction === "url" && (
            <Input
              placeholder="https://… или t.me/…"
              value={btnUrl}
              onChange={(e) => setBtnUrl(e.target.value)}
              className="font-mono"
            />
          )}
          {btnAction === "promo" && (
            <div className="w-56">
              <DarkSelect
                value={btnPromo}
                onChange={setBtnPromo}
                placeholder="— выбери промокод —"
                options={(promos.data?.items ?? []).map((p) => ({
                  value: p.code,
                  label: p.code,
                }))}
              />
            </div>
          )}
        </div>
      )}
      {btnAction !== "none" && btnAction !== "url" && (
        <div className="text-[11px] text-zinc-600 mt-1.5 font-mono break-all">
          {buildBtnUrl() ?? "…"}
        </div>
      )}
      {btnAction === "promo" && !botInfo.data?.username && (
        <div className="text-[11px] text-amber-400 mt-1">
          не удалось получить @бота — ссылка не соберётся
        </div>
      )}
    </div>
  );

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <PageHeader title="Рассылка" />

      {/* Шаг 1 — аудитория */}
      <div className="mb-5">
        <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-2">
          Аудитория
        </div>
        <div className="flex flex-wrap gap-2">
          {list.map((s) => {
            const active = !manualMode && segment === s.segment;
            return (
              <button
                key={s.segment}
                onClick={() => { setManualMode(false); setSegment(s.segment); }}
                title={s.description}
                className={`flex items-center gap-2.5 px-3.5 py-2 rounded-xl border transition-all ${
                  active
                    ? "bg-brand-soft border-brand/40 glow-ring"
                    : "border-white/[0.07] hover:border-white/[0.16] hover:bg-white/[0.02]"
                }`}
              >
                <span className={`text-sm ${active ? "text-brand font-semibold" : "text-zinc-200"}`}>
                  {s.label}
                </span>
                <span
                  className={`text-xs font-mono tabular-nums px-1.5 py-0.5 rounded-md ${
                    active ? "bg-brand/20 text-brand" : "bg-white/[0.05] text-zinc-400"
                  }`}
                >
                  {s.count}
                </span>
              </button>
            );
          })}
          <button
            onClick={() => setManualMode(true)}
            title="Точечно выбранным юзерам — для теста на себе или адресной отправки"
            className={`flex items-center gap-2.5 px-3.5 py-2 rounded-xl border transition-all ${
              manualMode ? "bg-brand-soft border-brand/40 glow-ring" : "border-white/[0.07] hover:border-white/[0.16] hover:bg-white/[0.02]"
            }`}
          >
            <span className={`text-sm ${manualMode ? "text-brand font-semibold" : "text-zinc-200"}`}>🎯 Произвольно</span>
            {manualUsers.length > 0 && (
              <span className={`text-xs font-mono tabular-nums px-1.5 py-0.5 rounded-md ${manualMode ? "bg-brand/20 text-brand" : "bg-white/[0.05] text-zinc-400"}`}>
                {manualUsers.length}
              </span>
            )}
          </button>
        </div>
        {!manualMode && selected?.description && (
          <div className="text-xs text-zinc-600 mt-2">{selected.description}</div>
        )}

        {manualMode && (
          <div className="mt-3 rounded-xl border border-white/[0.07] p-3 max-w-2xl">
            <div className="text-xs text-zinc-500 mb-2">
              Рассылка уйдёт только выбранным. Найдите себя, чтобы протестировать формат перед массовой отправкой.
            </div>
            <div className="relative">
              <Input value={uq} onChange={(e) => setUq(e.target.value)} placeholder="@username или telegram_id" />
              {uq.trim().length >= 2 && (
                <div className="absolute z-30 mt-1 w-full rounded-lg border border-white/[0.1] max-h-56 overflow-auto shadow-pop" style={{ backgroundColor: "#0c0e12" }}>
                  {userSearch.isLoading && <div className="px-3 py-2 text-xs text-zinc-500">Ищу…</div>}
                  {!userSearch.isLoading && (userSearch.data?.users ?? []).length === 0 && (
                    <div className="px-3 py-2 text-xs text-zinc-500">Не нашёл</div>
                  )}
                  {(userSearch.data?.users ?? []).map((u) => (
                    <button
                      key={u.telegram_id}
                      onClick={() => addUser(u)}
                      className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-white/[0.05]"
                    >
                      {u.username ? `@${u.username}` : `#${u.telegram_id}`}
                      <span className="text-xs text-zinc-500 font-mono ml-2">{u.telegram_id}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {manualUsers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {manualUsers.map((u) => (
                  <span key={u.telegram_id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/[0.05] text-xs text-zinc-200">
                    {u.username ? `@${u.username}` : `#${u.telegram_id}`}
                    <button
                      onClick={() => setManualUsers((prev) => prev.filter((p) => p.telegram_id !== u.telegram_id))}
                      className="text-zinc-500 hover:text-rose-400"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Шаг 2 — способ */}
      <div className="inline-flex items-center gap-1 mb-4 p-1 rounded-xl bg-white/[0.03] border border-white/[0.06]">
        {([
          ["text", "✍️ Быстрый текст"],
          ["bot", "🤖 Через бота"],
        ] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setMode(k)}
            className={`h-9 px-4 rounded-lg text-sm font-medium transition-all ${
              mode === k ? "bg-brand-soft text-brand glow-ring" : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === "text" && (
        <Card>
          <textarea
            className="w-full min-h-[220px] p-3.5 rounded-xl bg-black/25 border border-white/[0.08] text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-brand/60 focus:ring-2 focus:ring-brand/20 transition"
            placeholder="Что написать… (обычный текст, без форматирования)"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />

          {buttonPanel}

          <div className="flex items-center gap-3 flex-wrap mt-3">
            <Button
              disabled={!text.trim() || send.isPending || btnDisabled() || !audienceReady}
              onClick={() => {
                const url = buildBtnUrl();
                send.mutate({
                  segment: effectiveSegment,
                  text,
                  button_text: url ? btnText.trim() || defaultBtnLabel(btnAction) : null,
                  button_url: url,
                  idempotency_key: sendKey,
                });
              }}
            >
              {send.isPending ? "Отправляю…" : `Отправить → ${recipientCount} получателей`}
            </Button>
            {send.isError && (
              <span className="text-red-400 text-sm">{(send.error as Error).message}</span>
            )}
            {queued !== null && !send.isError && !draftId && (
              <span className="text-emerald-400 text-sm">
                Ушло в рассылку: {queued}. Идёт в фоне.
              </span>
            )}
          </div>
        </Card>
      )}

      {mode === "bot" && (
        <Card title="Через бота — форматирование, эмодзи, премиум-эмодзи">
          {!draftId && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-400">
                Этот режим переносит сообщение из вашего Telegram-клиента
                байт-в-байт. Поддерживается всё что умеет TG: жирный,
                курсив, подчёркнутый, зачёркнутый, моноширинный,
                цитаты, спойлеры, ссылки, обычные эмодзи и
                премиум-эмодзи.
              </p>
              <p className="text-sm text-zinc-400">
                Аудитория из выбранного выше:{" "}
                <code className="text-zinc-300">{manualMode ? `произвольно (${manualUsers.length})` : segment}</code> ·{" "}
                <b>{recipientCount}</b> получателей.
              </p>
              {buttonPanel}
              <Button
                disabled={startDraft.isPending || !audienceReady || btnDisabled()}
                onClick={() => {
                  const url = buildBtnUrl();
                  startDraft.mutate({
                    segment: effectiveSegment,
                    button_text: url ? btnText.trim() || defaultBtnLabel(btnAction) : null,
                    button_url: url,
                  });
                }}
              >
                {startDraft.isPending
                  ? "Запускаю…"
                  : "Создать рассылку → перейти в бота"}
              </Button>
              {startDraft.isError && (
                <div className="text-red-400 text-sm">
                  {(startDraft.error as Error).message}
                </div>
              )}
            </div>
          )}

          {draftId && draftState && (
            <div className="space-y-3">
              <div className="text-sm">
                <span className="text-zinc-500 uppercase tracking-wider mr-2">
                  draft #{draftState.id}
                </span>
                <StatusPill status={draftState.status} />
                {draftState.segment && (
                  <span className="ml-2 text-zinc-400">
                    сегмент:{" "}
                    <code className="text-zinc-300">{draftState.segment}</code>
                  </span>
                )}
              </div>

              {draftState.status === "awaiting_content" && (
                <div className="text-sm text-amber-300">
                  ⏳ Ждём ваше сообщение в личке бота. Откройте его в
                  Telegram, наберите/отформатируйте сообщение и пришлите.
                  Бот ответит «✅ Сообщение получено» — это поле обновится
                  само в течение 2 секунд.
                </div>
              )}

              {draftState.status === "ready" && (
                <>
                  <div className="text-sm text-emerald-400">
                    ✅ Сообщение получено и сохранено. Превью у вас в
                    личке бота. Подтвердите рассылку.
                  </div>
                  <div className="flex gap-3 flex-wrap">
                    <Button
                      disabled={confirmDraft.isPending}
                      onClick={() => confirmDraft.mutate(draftState.id)}
                    >
                      {confirmDraft.isPending
                        ? "Отправляю…"
                        : `Подтвердить и разослать (${draftState.queued ?? recipientCount})`}
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={cancelDraft.isPending}
                      onClick={() => cancelDraft.mutate(draftState.id)}
                    >
                      Отменить
                    </Button>
                  </div>
                </>
              )}

              {draftState.status === "sending" && (
                <div className="space-y-2">
                  <div className="text-sm text-blue-300">
                    🚚 Рассылка идёт… можно закрыть страницу.
                  </div>
                  <div className="text-xs text-zinc-400 tabular-nums">
                    Отправлено: <b>{draftState.sent_count ?? 0}</b> · Ошибок:{" "}
                    <b>{draftState.failed_count ?? 0}</b>
                    {draftState.queued ? ` из ${draftState.queued}` : ""}
                  </div>
                  <Button
                    variant="ghost"
                    disabled={cancelDraft.isPending}
                    onClick={() => cancelDraft.mutate(draftState.id)}
                  >
                    ⏹ Остановить
                  </Button>
                </div>
              )}

              {draftState.status === "done" && (
                <div className="space-y-1">
                  <div className="text-sm text-emerald-400">
                    ✅ Рассылка завершена.
                  </div>
                  <div className="text-xs text-zinc-400">
                    Отправлено: <b>{draftState.sent_count ?? 0}</b> ·
                    Ошибок: <b>{draftState.failed_count ?? 0}</b>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDraftId(null);
                      setDraftState(null);
                    }}
                  >
                    Создать новую
                  </Button>
                </div>
              )}

              {draftState.status === "canceled" && (
                <div className="space-y-1">
                  <div className="text-sm text-zinc-400">
                    Рассылка отменена.
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDraftId(null);
                      setDraftState(null);
                    }}
                  >
                    Создать новую
                  </Button>
                </div>
              )}

              {confirmDraft.isError && (
                <div className="text-red-400 text-sm">
                  {(confirmDraft.error as Error).message}
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: DraftState["status"] }) {
  const map: Record<DraftState["status"], { label: string; cls: string }> = {
    awaiting_content: {
      label: "ждём сообщение",
      cls: "bg-amber-900/40 text-amber-300 border-amber-700/50",
    },
    ready: {
      label: "готово к отправке",
      cls: "bg-emerald-900/40 text-emerald-300 border-emerald-700/50",
    },
    sending: {
      label: "рассылка идёт",
      cls: "bg-blue-900/40 text-blue-300 border-blue-700/50",
    },
    done: {
      label: "готово",
      cls: "bg-zinc-800 text-zinc-300 border-zinc-700",
    },
    canceled: {
      label: "отменена",
      cls: "bg-zinc-900 text-zinc-500 border-zinc-800",
    },
  };
  const s = map[status];
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${s.cls}`}
    >
      {s.label}
    </span>
  );
}
